import {AntibodyComponentSchemaInterfaceApiId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {deriveStepUiSchema} from 'pipelines/generalized-step-config-editor/step-config-settings/deriveStepUiSchema';
import {getStepTypeConfigShape} from 'pipelines/StepShapeUtils';

import EntryEditor from '../editors/custom-editors/EntryEditor';
import GeneticCodeEditor, {STANDARD_GENETIC_CODE} from '../editors/custom-editors/GeneticCodeEditor';
import GermlineEditor from '../editors/custom-editors/GermlineEditor';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {CUSTOM_COMPONENT_KEY, FIELD_ORDER_UI_SCHEMA_KEY} from '../StepConfigEditorTypes';

const NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA = deriveStepUiSchema(
  getStepTypeConfigShape(PipelineStepType.NDC_CREATE_PROTEINS_FROM_DATASET, 0).fields
);

export const NDC_CREATE_PROTEINS_FROM_DATASET_V0_UI_SCHEMA = {
  ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA,
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'proteinSchemaId',
    'dnaDomainSchemaId',
    'dnaPairSchemaId',
    'dnaChainSchemaId',
    'aaPairSchemaId',
    'geneticCode',
    'germlineId',
    'folderId',
    // The receipt inserts an entry section, so only offer it where entry sections are supported,
    // matching the spreadsheet-import UI (FinalizePane).
    ...(BenchlingConfig.ELN_ENABLE_ENTRY_SECTIONS ? (['documentId'] as const) : []),
  ],
  proteinSchemaId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.proteinSchemaId,
    displayName: 'Protein schema',
    fieldSettings: {filterToValidProteinSchemas: true},
  },
  germlineId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.germlineId,
    displayName: 'Germline',
    infoText: 'Applied to all domains that require germline assignment for the selected protein format',
    [CUSTOM_COMPONENT_KEY]: GermlineEditor,
  },
  dnaDomainSchemaId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.dnaDomainSchemaId,
    displayName: 'DNA domain schema',
    infoText: 'Required when the input dataset includes DNA sequences',
    fieldSettings: {implementsSchemaInterfaceId: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID},
  },
  dnaPairSchemaId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.dnaPairSchemaId,
    displayName: 'DNA variable pair schema',
    infoText: 'Required when the input dataset includes DNA sequences',
    fieldSettings: {implementsSchemaInterfaceId: AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID},
  },
  aaPairSchemaId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.aaPairSchemaId,
    displayName: 'AA variable pair schema',
    fieldSettings: {implementsSchemaInterfaceId: AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID},
  },
  dnaChainSchemaId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.dnaChainSchemaId,
    displayName: 'DNA chain schema',
    infoText: 'Required when the input dataset includes DNA sequences',
    fieldSettings: {implementsSchemaInterfaceId: AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID},
  },
  geneticCode: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.geneticCode,
    displayName: 'Genetic code',
    infoText: 'Select genetic code to translate DNA domains into AA domains',
    fieldSettings: {
      defaultValue: STANDARD_GENETIC_CODE,
    },
    [CUSTOM_COMPONENT_KEY]: GeneticCodeEditor,
  },
  folderId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.folderId,
    displayName: 'Folder',
    fieldSettings: {
      showRegistries: false,
    },
  },
  documentId: {
    ...NDC_CREATE_PROTEINS_FROM_DATASET_BASE_UI_SCHEMA.documentId,
    displayName: 'Entry',
    infoText: 'Optionally send a receipt of protein creation to a notebook entry',
    [CUSTOM_COMPONENT_KEY]: EntryEditor,
  },
} as StepTypeConfigUISchemaV2<PipelineStepType.NDC_CREATE_PROTEINS_FROM_DATASET, 0>;
